import { z } from 'zod';
import { ApiError } from './client';
import { ApiCharacterZ, FixedReaderContextZ, ReaderDisplayModeZ } from './fixed-reader-context-schema';
import type { components } from './schema';
import { SceneResponseZ } from './stream-schema';
import { getIndexedDBCache } from './persistence/indexedDBCache';

type Scene = components['schemas']['Scene'];
type Scenario = components['schemas']['Scenario'];

const ScenarioResponseZ = z.object({
  id: z.string(),
  title: z.string(),
  characters: z.array(ApiCharacterZ),
  generationMode: z.string(),
  endingBackgroundColor: z.string().nullable().optional(),
  endingTextColor: z.string().nullable().optional(),
  playScreenBackgroundColor: z.string().nullable().optional(),
  playPanelBackgroundColor: z.string().nullable().optional(),
  feedbackLoopEnabled: z.boolean().optional(),
  adjustmentStrength: z.number().optional(),
  displayMode: ReaderDisplayModeZ.optional(),
  fixedContext: FixedReaderContextZ.optional(),
});

function readerEnvelopeError(reason: string): ApiError {
  return new ApiError(502, {
    type: '/errors/invalid-reader-envelope',
    title: 'Reader response unavailable',
    status: 502,
    detail: reason === 'fixed_context_required'
      ? 'Online refresh required for fixed reader context.'
      : 'Reader response could not be verified.',
    reason,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function sceneVersion(value: unknown): string | undefined {
  return isRecord(value) && typeof value.fixedContentVersionId === 'string'
    ? value.fixedContentVersionId
    : undefined;
}

export function sceneEnvelopeError(value: unknown, generationMode?: string, expectedVersion?: string): ApiError | null {
  const parsed = SceneResponseZ.safeParse(value);
  if (!parsed.success) return readerEnvelopeError('invalid_scene_schema');
  const scene = parsed.data;
  // 最新Scenarioのmodeは旧payloadの分類にだけ使い、contextは補完しない。
  const fixed = generationMode === 'pre-baked'
    || (isRecord(value) && value.generationMode === 'pre-baked')
    || scene.fixedContentNodeKey != null
    || scene.fixedContentVersionId != null
    || scene.fixedContext !== undefined;
  if (fixed && (!scene.fixedContentVersionId || scene.fixedContext === undefined)) {
    return readerEnvelopeError('fixed_context_required');
  }
  if (scene.fixedContext !== undefined && scene.fixedContentVersionId !== scene.fixedContext.fixedContentVersionId) {
    return readerEnvelopeError('fixed_version_mismatch');
  }
  if (expectedVersion !== undefined && scene.fixedContentVersionId !== expectedVersion) {
    return readerEnvelopeError('fixed_version_mismatch');
  }
  return null;
}

export function scenarioEnvelopeError(value: unknown, expectedId?: string): ApiError | null {
  const parsed = ScenarioResponseZ.safeParse(value);
  if (!parsed.success) return readerEnvelopeError('invalid_scenario_schema');
  const scenario = parsed.data;
  if (scenario.generationMode === 'pre-baked' && scenario.fixedContext === undefined) {
    return readerEnvelopeError('fixed_context_required');
  }
  if ((expectedId !== undefined && scenario.id !== expectedId)
    || (scenario.fixedContext !== undefined && scenario.fixedContext.scenario.id !== scenario.id)) {
    return readerEnvelopeError('fixed_scenario_mismatch');
  }
  return null;
}

// メモリcacheにはundefinedのoptional fieldもあり得るため、wireと同じ省略形へ揃える。
function omitUndefined(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(omitUndefined);
  if (isRecord(value)) {
    return Object.fromEntries(Object.entries(value)
      .filter(([, field]) => field !== undefined)
      .map(([key, field]) => [key, omitUndefined(field)]));
  }
  return value;
}

function isSceneEnvelope(value: unknown, generationMode?: string, expectedVersion?: string): value is Scene {
  return sceneEnvelopeError(value, generationMode, expectedVersion) === null;
}

function isScenarioEnvelope(value: unknown, expectedId?: string): value is Scenario {
  return scenarioEnvelopeError(value, expectedId) === null;
}

export function readSceneEnvelope(value: unknown, generationMode?: string, expectedVersion?: string): Scene {
  const error = sceneEnvelopeError(value, generationMode, expectedVersion);
  if (error !== null) throw error;
  const canonical = omitUndefined(SceneResponseZ.parse(value));
  if (isSceneEnvelope(canonical, generationMode, expectedVersion)) return canonical;
  throw readerEnvelopeError('invalid_scene_schema');
}

export function readScenarioEnvelope(value: unknown, expectedId?: string): Scenario {
  const error = scenarioEnvelopeError(value, expectedId);
  if (error !== null) throw error;
  const canonical = omitUndefined(ScenarioResponseZ.parse(value));
  if (isScenarioEnvelope(canonical, expectedId)) return canonical;
  throw readerEnvelopeError('invalid_scenario_schema');
}

export function canUseReaderCache(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.problem.type === '/errors/fixed-content-unavailable'
      || error.problem.type === '/errors/fixed-asset-delivery-unavailable'
      || error.problem.type === '/errors/invalid-reader-envelope') return false;
    return error.status === 0 || (error.status >= 500 && error.status <= 599);
  }
  return error instanceof TypeError;
}

// IDB自体の利用不可は既存方針でdegradeする。検証エラーはこのcatchの外へ伝える。
export async function readReaderCache(namespace: 'scene' | 'scenario', id: string): Promise<unknown> {
  try {
    return (await getIndexedDBCache().get<unknown>(namespace, id))?.value ?? null;
  } catch {
    return null;
  }
}

export async function knownReaderSceneVersion(playlineId: string, memoryScene: unknown, generationMode?: string): Promise<string | undefined> {
  const memoryVersion = sceneEnvelopeError(memoryScene, generationMode) === null ? sceneVersion(memoryScene) : undefined;
  const persistedScene = await readReaderCache('scene', playlineId);
  // 旧/不正cacheの版値は根拠にせず、オンラインから正常envelopeを取得できるようにする。
  const persistedVersion = sceneEnvelopeError(persistedScene, generationMode) === null ? sceneVersion(persistedScene) : undefined;
  if (memoryVersion !== undefined && persistedVersion !== undefined && memoryVersion !== persistedVersion) {
    throw readerEnvelopeError('fixed_version_mismatch');
  }
  return memoryVersion ?? persistedVersion;
}

export function persistReaderEnvelope(namespace: 'scene' | 'scenario', id: string, value: Scene | Scenario): void {
  void getIndexedDBCache().set(namespace, id, value).catch(() => {
    // quota/private modeによる永続化失敗は取得済みの有効payloadを維持する。
  });
}
